import {
    Banner,
    BannerDescription,
    BannerEyebrow,
    BannerLinks,
    BannerText,
    BannerTextContainer,
    BannerTitle,
    Box,
    Button,
    FlexBox,
    FluidImage,
    H1,
    P,
} from '@vp/swan';
import styled from 'styled-components';
import { __, __image } from '@99designs/i18n';
import { usePricingProviderContext } from '@99designs/pricing';
import { CloudinaryImage } from '../CloudinaryImage';
import { HeaderPrice } from '../HeaderPrice';

/**
 * A darker variant of midnight that has been approved by design for the holiday banner only.
 * There is no SWAN token for this colour.
 */
const HOLIDAY_BANNER_BACKGROUND = '#1D273C';

const HolidayBanner = styled(Banner)`
    --swan-public-marketing-background: ${HOLIDAY_BANNER_BACKGROUND};
`;

interface HolidayHeaderProps {
    browseDesignServicesCtaOnClick: () => void;
    howItWorksCtaOnClick: () => void;
}

export function HolidayHeader({
    browseDesignServicesCtaOnClick,
    howItWorksCtaOnClick,
}: HolidayHeaderProps) {
    const { lowestPrice, loading: priceLoading } = usePricingProviderContext();

    return (
        <HolidayBanner darkMode data-testid="hub-holiday-hero">
            <BannerTextContainer>
                <BannerText data-testid="hub-holiday-hero-content">
                    <BannerEyebrow data-testid="hub-holiday-hero-eyebrow">
                        {__('Design Services')}
                    </BannerEyebrow>
                    <BannerTitle
                        as={H1}
                        fontSkin="title-headline"
                        data-testid="hub-holiday-hero-title"
                    >
                        {__('Your holiday ideas made real with our design and print experts.')}
                    </BannerTitle>
                    <BannerDescription data-testid="hub-holiday-hero-description">
                        <P>
                            {__(
                                'Our professional designers can turn your holiday ‘what ifs?’ into unforgettable moments – creating custom holiday cards, wall calendars and gifts.'
                            )}
                        </P>
                    </BannerDescription>
                    <Box fontSkin="title-item" data-testid="hub-holiday-hero-price">
                        <HeaderPrice price={lowestPrice} loading={priceLoading} />
                    </Box>
                    <BannerLinks data-testid="hub-holiday-hero-actions">
                        <Button
                            skin="primary"
                            onClick={browseDesignServicesCtaOnClick}
                            data-testid="hub-holiday-hero-browse-services-cta"
                        >
                            {__('Browse design services')}
                        </Button>
                        <Button
                            skin="secondary"
                            onClick={howItWorksCtaOnClick}
                            data-testid="hub-holiday-hero-how-it-works-cta"
                        >
                            {__('How it works')}
                        </Button>
                    </BannerLinks>
                </BannerText>
            </BannerTextContainer>
            <FlexBox
                style={{ flexBasis: '50%' }}
                flexDirection="column"
                justifyContent="center"
                alignItems="flex-start"
                data-testid="hub-holiday-hero-image"
            >
                <CloudinaryImage
                    render={(props) => (
                        <FluidImage
                            src={props.src}
                            srcSet={props.srcSet}
                            alt=""
                            role="presentation"
                        />
                    )}
                    imagePath={__image('expert_services/HubPage/holiday/hero-image-2026')}
                    width={686}
                />
            </FlexBox>
        </HolidayBanner>
    );
}
